(() => {
  const initGallery = root => {
    if (root.dataset.wrcGalleryInitialized === 'true') return;

    const track = root.querySelector('[data-wrc-gallery-track]');
    const slides = [...root.querySelectorAll('[data-wrc-gallery-slide]')];
    const thumbnails = [...root.querySelectorAll('[data-wrc-gallery-thumbnail]')];
    const current = root.querySelector('[data-wrc-gallery-current]');
    if (!track || slides.length === 0) return;

    root.dataset.wrcGalleryInitialized = 'true';
    const desktop = window.matchMedia('(min-width: 900px)');
    let activeIndex = 0;
    let scrollFrame;

    const setActive = index => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', String(isActive));
      });
      if (current) current.textContent = activeIndex + 1;
    };

    const scrollToIndex = index => {
      setActive(index);
      track.scrollTo({ left: slides[activeIndex].offsetLeft, behavior: 'smooth' });
    };

    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => setActive(Number(thumbnail.dataset.galleryIndex)));
    });

    track.addEventListener('scroll', () => {
      if (desktop.matches) return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        setActive(index);
      });
    }, { passive: true });

    track.addEventListener('keydown', event => {
      let targetIndex;
      if (event.key === 'ArrowLeft') targetIndex = activeIndex - 1;
      if (event.key === 'ArrowRight') targetIndex = activeIndex + 1;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = slides.length - 1;
      if (targetIndex === undefined) return;
      event.preventDefault();
      if (desktop.matches) setActive(targetIndex);
      else scrollToIndex(targetIndex);
    });

    desktop.addEventListener('change', event => {
      if (!event.matches) track.scrollTo({ left: slides[activeIndex].offsetLeft });
      setActive(activeIndex);
    });
  };

  const initProduct = root => {
    if (root.dataset.wrcProductInitialized === 'true') return;
    root.dataset.wrcProductInitialized = 'true';
    const price = root.querySelector('[data-wrc-current-price]');
    const compare = root.querySelector('[data-wrc-compare-price]');
    const addButton = root.querySelector('[data-wrc-add-to-cart]');
    const addLabel = root.querySelector('[data-wrc-add-label]');
    const sticky = document.querySelector('[data-wrc-sticky-purchase]');
    const stickyPrice = sticky?.querySelector('[data-wrc-sticky-price]');
    const stickyVariant = sticky?.querySelector('[data-wrc-sticky-variant]');
    const stickyButton = sticky?.querySelector('[data-wrc-sticky-add]');
    const stickyStatus = sticky?.querySelector('[data-wrc-sticky-status]');

    root.querySelectorAll('.wrc-size-card__input').forEach(input => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        price.textContent = input.dataset.price;
        compare.textContent = input.dataset.comparePrice || '';
        compare.hidden = !input.dataset.comparePrice;
        const available = input.dataset.available === 'true';
        addButton.disabled = !available;
        addButton.setAttribute('aria-disabled', String(!available));
        addLabel.textContent = available ? 'Add to cart' : 'Sold out';
        if (stickyPrice) stickyPrice.textContent = input.dataset.price;
        if (stickyVariant) stickyVariant.textContent = input.dataset.variantTitle;
        if (stickyButton) {
          stickyButton.disabled = !available;
          stickyButton.textContent = available ? 'Add to cart' : 'Sold out';
        }
        root.dataset.variantId = input.value;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', url);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });

    const gallery = root.querySelector('[data-wrc-gallery]');
    if (gallery) initGallery(gallery);

    const sizeHelp = root.querySelector('[data-wrc-size-help]');
    const sizeHelpOpen = root.querySelector('[data-wrc-size-help-open]');
    const sizeHelpClose = root.querySelector('[data-wrc-size-help-close]');
    if (sizeHelp && sizeHelpOpen && typeof sizeHelp.showModal === 'function') {
      sizeHelpOpen.addEventListener('click', () => sizeHelp.showModal());
      const closeSizeHelp = () => {
        sizeHelp.close();
        sizeHelpOpen.focus();
      };
      sizeHelpClose.addEventListener('click', closeSizeHelp);
      sizeHelp.addEventListener('click', event => {
        if (event.target === sizeHelp) closeSizeHelp();
      });
      sizeHelp.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        closeSizeHelp();
      });
    }

    if (sticky && addButton && stickyButton) {
      const observer = new IntersectionObserver(entries => {
        const originalVisible = entries[0]?.isIntersecting;
        const originalIsAboveViewport = addButton.getBoundingClientRect().top < 0;
        sticky.hidden = originalVisible || !originalIsAboveViewport;
      }, { threshold: 0.05 });
      observer.observe(addButton);
      stickyButton.addEventListener('click', () => {
        if (addButton.disabled) return;
        addButton.click();
        if (stickyStatus) stickyStatus.textContent = 'Selected item submitted to cart.';
      });
    }
  };

  const initQuestions = section => {
    if (section.dataset.wrcQuestionsInitialized === 'true') return;
    section.dataset.wrcQuestionsInitialized = 'true';
    const questions = [...section.querySelectorAll('details')];
    questions.forEach(question => {
      question.addEventListener('toggle', () => {
        if (!question.open) return;
        questions.forEach(other => { if (other !== question) other.open = false; });
      });
      const summary = question.querySelector('summary');
      summary?.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        question.open = !question.open;
      });
    });
  };

  const initRecommendations = section => {
    if (section.dataset.loaded === 'true') return;
    fetch(section.dataset.url)
      .then(response => response.text())
      .then(html => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const replacement = documentFragment.querySelector('[data-wrc-recommendations]');
        if (replacement) section.innerHTML = replacement.innerHTML;
        section.dataset.loaded = 'true';
      })
      .catch(() => {});
  };

  const init = scope => {
    scope.querySelectorAll('[data-wrc-product]').forEach(initProduct);
    scope.querySelectorAll('[data-wrc-recommendations][data-url]').forEach(initRecommendations);
    scope.querySelectorAll('[data-wrc-questions]').forEach(initQuestions);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
